import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import CardsContainer from "@/components/CardsContainer";
import CardsList from "@/components/CardsList";
import CardsTable from "@/components/CardsTable";
import VLFSelect from "@/components/Input/SelectNew";
import VLFTextField from "@/components/Input/TextField";
import {RosterAPI} from "@/api/roster";
import AnthropometricDataPage from "@/pages/AnthropometricData";

import {AthleticTestAPI} from "@/api/athletic_test";
import {PlayerAPI} from "@/api/player";

import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";

import {
	ERROR_OPERATION,
	getInternalClubId,
	SUCCESSFUL_OPERATION,
	dateFormat,
} from "@/utils/constants";
import {getCurrentCategory} from "@/utils/functions";
import {useCategories} from "@/utils/hooks";

import "./index.scss";

const VIEW_OPTIONS = [
	{value: "tests", label: "Test"},
	{value: "anthropometric_data", label: "Dati antropometrici"},
];

function AthleticTestsPage(props) {
	const {handleNotification} = props;

	const navigate = useNavigate();

	const seasonId = useSelector((state) => state?.season?.id);

	const [currentSwitchView, setCurrentSwitchView] = useState("tests");

	const [loading, setLoading] = useState(true);
	const [tests, setTests] = useState([]);
	const [rosters, setRosters] = useState([]);
	const [roster, setRoster] = useState(null);
	const [search, setSearch] = useState("");
	const rosterId =
		roster && roster.seasonId === seasonId ? roster.value : undefined;

	const [currentDate, setCurrentDate] = useState();
	const [playersList, setPlayersList] = useState([]);

	const {categories} = useCategories({
		setLoading: false,
		handleNotification: undefined,
		onlyPro: true,
		formatted: true,
	});

	useEffect(() => {
		let active = true;
		setRosters([]);
		RosterAPI.list({seasonId, clubId: getInternalClubId(), prospect: false})
			.then((response) => {
				if (active)
					setRosters(
						(response || [])
							.filter((row) => !row.isPool)
							.map((row) => ({value: row.id, label: row.name, seasonId})),
					);
			})
			.catch(() => {
				if (active) handleNotification(ERROR_OPERATION, "error");
			});
		return () => {
			active = false;
		};
	}, [seasonId, handleNotification]);

	useEffect(() => {
		let active = true;
		setLoading(true);
		AthleticTestAPI.list({seasonId, rosterId})
			.then((response) => {
				if (active) setTests(response || []);
			})
			.catch(() => {
				if (active) {
					setTests([]);
					handleNotification(ERROR_OPERATION, "error");
				}
			})
			.finally(() => {
				if (active) setLoading(false);
			});
		return () => {
			active = false;
		};
	}, [seasonId, rosterId, handleNotification]);

	const saveGrowths = async () => {
		setLoading(true);
		try {
			await PlayerAPI.createGrowths(
				playersList.map((p) => {
					return {
						date: moment(currentDate).format("YYYY-MM-DD"),
						playerId: p.player.value,
						height: p.height || 0,
						sittingHeight: p.sittingHeight || 0,
						weight: p.weight || 0,
					};
				}),
			);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setCurrentDate();
			setPlayersList([]);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const normalizeName = (value) =>
		(value || "")
			.toLocaleLowerCase("it-IT")
			.normalize("NFD")
			.replace(/[\u0300-\u036f]/g, "");
	const terms = normalizeName(search).trim().split(/\s+/).filter(Boolean);
	const visibleTests = tests.filter((test) =>
		terms.every((term) =>
			normalizeName(
				[test.player?.firstName, test.player?.lastName]
					.filter(Boolean)
					.join(" "),
			).includes(term),
		),
	);

	const listConfiguration = {
		data: visibleTests.map((test) => ({
			...test,
			currentCategory: getCurrentCategory(categories, test),
		})),
		onClick: (test) => navigate("/athletic-tests/" + test.id),
	};

	const columns = [
		{
			label: "Data",
			key: "testDate",
			customRender: (item) => moment(item.testDate).format(dateFormat),
			small: true,
		},
		{
			label: "Giocatore",
			key: "playerId",
			customRender: (item) =>
				item?.player
					? item.player.firstName + " " + item.player.lastName
					: "---",
			align: "start",
		},
		{
			label: "Tipologia",
			key: "testType",
			align: "start",
			small: true,
		},
		{
			label: "Descrizione",
			key: "description",
			align: "start",
			customRender: (item) => (
				<span
					className="athletic-test-description"
					title={item.description || ""}
				>
					{item.description || "—"}
				</span>
			),
		},
	];

	const mainActions = [];

	if (currentSwitchView === "tests") {
		mainActions.push({
			icon: <AddIcon />,
			title: "Aggiungi test",
			onClick: () => navigate("/athletic-tests/new"),
		});
	}

	if (currentSwitchView === "anthropometric_data") {
		mainActions.push({
			icon: <SaveIcon />,
			title: "Salva",
			onClick: saveGrowths,
			disabled: !currentDate || !playersList?.length,
		});
	}

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				current="Test atletici"
				listElements={VIEW_OPTIONS.map((o) => ({
					...o,
					onClick: () => setCurrentSwitchView(o.value),
				}))}
				currentListElement={currentSwitchView}
				actions={<MainActions actions={mainActions} />}
			/>
			{currentSwitchView === "tests" && (
				<div className="athletic-tests-filters">
					<div>
						<VLFSelect
							label="Rosa"
							placeholder="Tutte le mie rose"
							value={rosterId ? roster : null}
							options={rosters}
							onChange={setRoster}
							isClearable
							noMargin
						/>
					</div>
					<div>
						<VLFTextField
							label="Giocatore"
							placeholder="Cerca nome o cognome"
							value={search}
							onChange={(event) => setSearch(event.target.value)}
							margin="none"
							size="small"
						/>
					</div>
					<small>{visibleTests.length} test</small>
				</div>
			)}
			{currentSwitchView === "tests" ? (
				<CardsContainer
					tableComponent={
						<CardsTable
							fullWidth
							breadcrumbs
							configurationColumns={columns}
							data={visibleTests}
							handleClick={(item) => {
								navigate("/athletic-tests/" + item.id);
							}}
							defaultSortedField="testDate"
							defaultSortedOrder="descend"
						/>
					}
					listComponent={
						<CardsList
							type="test"
							breadcrumbs
							listConfiguration={listConfiguration}
							defaultSortedField="testDate"
							defaultSortedOrder="descend"
						/>
					}
				/>
			) : null}
			{currentSwitchView === "anthropometric_data" ? (
				<AnthropometricDataPage
					currentDate={currentDate}
					setCurrentDate={setCurrentDate}
					playersList={playersList}
					setPlayersList={setPlayersList}
				/>
			) : null}
		</Page>
	);
}

export default AthleticTestsPage;
